import axios from '../axiosInstance';

import qs from "qs";

export async function getEnabledAccounts({
  search = '',
  pageSize = 50,
  lastId = null,
  transTypeId = null,
  assAccountId = null,
  searchFields = null,
  signal = null,
} = {}) {
  const params = { search, pageSize, lastId };
  if (transTypeId != null) params.transTypeId = transTypeId;
  if (assAccountId != null) params.assAccountId = assAccountId;
  if (Array.isArray(searchFields) && searchFields.length) {
    params.searchFields = searchFields;
  }
  const { data } = await axios.get('/Accounts/getEnabled', {
    params,
    paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat', skipNulls: true }),
    signal,
  });
  return data; // { items, lastId, hasMore }
}

export const getAccountLookup = (signal = null) =>
  axios.get('/Accounts/enabled', { signal }).then(res => res.data);

export const createAccount = (account, signal = null) =>
  axios.post('/Accounts', account, { signal }).then(res => res.data);

export const updateAccount = (id, account, signal = null) =>
  axios.put(`/Accounts/${id}`, account, { signal }).then(res => res.data);

export const deleteAccount = (id, signal = null) =>
  axios.delete(`/Accounts/${id}`, { signal }).then(res => res.data);

export const restoreAccount = (id, signal = null) =>
  axios.post(`/Accounts/${id}/restore`, null, { signal }).then(res => res.data);

export const disableAccount = (id, signal = null) =>
  axios.put(`/Accounts/Disable/${id}`, null, { signal }).then(res => res.data);

export const getAccountById = (id, signal = null) =>
  axios.get(`/Accounts/${id}`, { signal }).then(res => res.data);

/**
 * Keyset-paginated Master Accounts for the lookup.
 * Backend: GET /api/Accounts/getMasterAccounts?search=&pageSize=&lastId=
 * Search matches name, shortName, erpCode, email, and address1–4.
 * Returns: { items, lastId, hasMore }
 */
export async function getEnabledMasterAccounts({ search = '', pageSize = 50, lastId = null, signal = null } = {}) {
  const { data } = await axios.get('/Accounts/getmasteraccounts', {
    params: { search, pageSize, lastId },
    signal,
  });
  return data;
}


export async function queryEnabledAccounts({
  search,
  pageSize,
  cursor,
  sortBy,
  sortDir,
  filterModel,
  transTypeId,
  iox,
  mastersOnly = false,
  signal = null,
}) {
  const params = {};
  if (transTypeId != null) params.transTypeId = transTypeId;
  if (iox) params.iox = iox;
  if (mastersOnly) params.mastersOnly = true;

  const body = {
    search: search ?? null,
    pageSize,
    cursor: cursor ?? null,
    sortBy,   // e.g. "ADDRESS1"
    sortDir,  // "asc" | "desc"
    filterModel: filterModel ?? { items: [], logicOperator: 'and' },
  };

  const { data } = await axios.post(`/Accounts/queryEnabled`, body, { params, signal });
  return data; // { items, cursor, hasMore }
}

// row level grid refreshing
export async function getAccountsByIds(ids, gridReq, signal = null) {
  const res = await axios.post(
    "/Accounts/byIdsEnabled",
    gridReq,
    {
      params: { ids },
      paramsSerializer: (params) => qs.stringify(params, { arrayFormat: "repeat" }), // ids=102&ids=103
      signal,
    }
  );
  return res.data;
}

/**
 * Fetch enabled accounts by their IDs (for PPD filtering).
 * @param {number[]} ids - Array of account IDs to fetch
 * @returns {Promise<{items: Array}>} Promise resolving to accounts data
 */
export async function getEnabledAccountsByIds(ids, signal = null, { transTypeId = null, iox = null } = {}) {
  if (!ids || ids.length === 0) return { items: [] };
  const params = { ids };
  if (transTypeId != null) params.transTypeId = transTypeId;
  if (iox) params.iox = iox;

  const res = await axios.post(
    "/Accounts/byIdsEnabled",
    { filterModel: { items: [], logicOperator: 'and' } }, // Empty gridReq
    {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: "repeat" }),
      signal,
    }
  );
  return res.data;
}

/**
 * Fetch lightweight account data by IDs (only id + display fields for weighing console).
 * Returns minimal data: id, name, erpCode, address1-4 for display purposes.
 * This is cheaper than full account objects when you only need display fields.
 * @param {number[]} ids - Array of account IDs to fetch
 * @returns {Promise<Array>} Promise resolving to array of lightweight account objects
 */
export async function getEnabledAccountsByIdsLightweight(ids, signal = null) {
  if (!ids || ids.length === 0) return [];
  const res = await axios.post(
    "/Accounts/byIdsWeighingConsole",
    ids,
    { signal }
  );
  return res.data; // Returns array directly: [{ id, name, erpCode, address1, address2, address3, address4 }]
}
